<script setup lang="ts">
import type { NodeViewProps } from '@tiptap/vue-3';
import { NodeViewWrapper } from '@tiptap/vue-3';
import { ImageOff, Trash2 } from 'lucide-vue-next';
import { computed, inject, ref } from 'vue';
import { Button } from '@/components/ui/button';

const props = defineProps<NodeViewProps>();

// ── Injected context ───────────────────────────────────────────────────
const figureCounters = inject<Readonly<{ value: ReadonlyMap<string, number> }>>(
    'editorFigureCounters',
);
const editorAssets = inject<{
    value: { id: string; src: string; alt?: string; caption?: string }[];
}>('editorAssets');

// ── Live figure number ─────────────────────────────────────────────────
const figureNumber = computed(() => {
    const map = figureCounters?.value;
    const id = props.node.attrs.figureId as string | null;

    if (!map || !id) {
        return '?';
    }

    return map.get(id) ?? '?';
});

// ── Asset src resolution ───────────────────────────────────────────────
const assetSrc = computed(() => {
    const assetId = props.node.attrs.assetId as string | null;

    if (!assetId) {
        return null;
    }

    const list = editorAssets?.value ?? [];

    return list.find((a) => a.id === assetId)?.src ?? null;
});

// ── Caption editing ────────────────────────────────────────────────────
const caption = computed(() => (props.node.attrs.caption as string) || '');

function updateCaption(e: Event) {
    props.updateAttributes({ caption: (e.target as HTMLInputElement).value });
}

// ── Alt text ───────────────────────────────────────────────────────────
const alt = computed(() => (props.node.attrs.alt as string) || '');

// ── Width ──────────────────────────────────────────────────────────────
const imgWidth = computed(() => {
    const w = props.node.attrs.width as number | null;

    return w ? `${w}px` : '100%';
});

// ── Deletion ───────────────────────────────────────────────────────────
const showDelete = ref(false);
function deleteFigure() {
    props.deleteNode();
}
</script>

<template>
    <NodeViewWrapper
        class="figure-wrapper group my-6"
        @mouseenter="showDelete = true"
        @mouseleave="showDelete = false"
    >
        <!-- ── Image ──────────────────────────────────────────────────── -->
        <div class="relative flex justify-center">
            <img
                v-if="assetSrc"
                :src="assetSrc"
                :alt="alt"
                :style="{ width: imgWidth, maxWidth: '100%', display: 'block' }"
                class="rounded"
                draggable="false"
            />
            <!-- Fallback when asset not yet resolved -->
            <div
                v-else
                class="flex h-32 w-full items-center justify-center rounded border border-dashed border-border bg-muted"
            >
                <ImageOff class="h-8 w-8 text-muted-foreground" />
            </div>

            <!-- Delete button overlay -->
            <Transition>
                <Button
                    v-if="showDelete"
                    variant="destructive"
                    size="icon"
                    class="absolute top-2 right-2 h-7 w-7 opacity-90"
                    type="button"
                    @click="deleteFigure"
                >
                    <Trash2 class="h-3.5 w-3.5" />
                </Button>
            </Transition>
        </div>

        <!-- ── Caption ───────────────────────────────────────────────── -->
        <div class="mt-1.5 flex items-center gap-2">
            <span class="shrink-0 text-sm font-semibold text-foreground">
                Figure&nbsp;{{ figureNumber }}&nbsp;:
            </span>
            <input
                type="text"
                :value="caption"
                placeholder="Ajouter une légende…"
                class="min-w-0 flex-1 border-b border-transparent bg-transparent text-sm text-muted-foreground placeholder:text-muted-foreground/50 focus:border-border focus:outline-none"
                @input="updateCaption"
            />
        </div>
    </NodeViewWrapper>
</template>

<style scoped>
.v-enter-active,
.v-leave-active {
    transition: opacity 150ms ease;
}
.v-enter-from,
.v-leave-to {
    opacity: 0;
}
</style>
